import { useEffect, useRef, useState } from "react";

import {
  Box,
  Button,
  Circle,
  HStack,
  Icon,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack
} from "@chakra-ui/react";

import { ChevronDownIcon } from "@chakra-ui/icons";

import useTermsOfService from "@/hooks/features/useTermsOfService";

const TermsOfService = () => {
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(false);
  const scrollBoxRef = useRef<HTMLDivElement>(null);
  const {
    handleAcceptTermsOfService,
    showTermsOfService,
    setShowTermsOfService,
    isAcceptingTermsOfService
  } = useTermsOfService();

  useEffect(() => {
    const handleScrollMessage = (event: MessageEvent) => {
      if (event.data === "scrolled-to-end") {
        setIsScrolledToEnd(true);
      }
    };
    window.addEventListener("message", handleScrollMessage);

    return () => {
      window.removeEventListener("message", handleScrollMessage);
    };
  }, []);

  const handleAccept = () => {
    handleAcceptTermsOfService();
  };

  const handleScroll = () => {
    const scrollBox = scrollBoxRef.current;
    if (scrollBox) {
      const { scrollTop, scrollHeight, clientHeight } = scrollBox;
      if (scrollTop + clientHeight >= scrollHeight) {
        setIsScrolledToEnd(true);
      }
    }
  };

  const scrollToBottom = () => {
    const scrollBox = scrollBoxRef.current;
    if (scrollBox) {
      scrollBox.scrollTo({
        top: scrollBox.scrollHeight,
        behavior: "smooth"
      });
    }
    setIsScrolledToEnd(true);
  };

  return (
    <Modal
      isCentered
      closeOnOverlayClick={false}
      closeOnEsc={false}
      size="4xl"
      isOpen={showTermsOfService}
      onClose={() => setShowTermsOfService(false)}
    >
      <ModalOverlay />
      <ModalContent px={10} py={6}>
        <ModalHeader fontWeight={700} pb={2} fontSize={20}>
          Terms of Service
        </ModalHeader>
        <ModalBody pt={0}>
          <Text fontSize={13} fontWeight={500} mb={1}>
            To view the full Terms of Service please visit&nbsp;
            <Link
              href="https://jenesys.co/legal"
              target="_blank"
              color="blue.500"
            >
              https://jenesys.co/legal
            </Link>
          </Text>
          <Box
            className="termsOfServiceBox"
            height="350px"
            overflowY="scroll"
            p={2}
            fontSize={14}
            borderBottom="1px solid"
            borderColor="gray.200"
            onScroll={handleScroll}
            ref={scrollBoxRef}
          >
            <Text>
              <Box as="span" fontWeight={600}>
                Key Acknowledgements
              </Box>
              <br />
              1.We may change these terms at any time without notice. Any such
              changes shall take effect on the next occasion that you make use
              of the Services. Any such new terms may be displayed on-screen
              when you next use the Services, and you may be required to read
              and accept them in order to continue your use of the Services.
              <br />
              <br />
              2. From time to time, we may automatically update the Services to
              improve performance, enhance functionality, reflect changes to the
              operating system or address security issues. Depending on the
              update, you may not be able to use the Services until you have
              downloaded, streamed or accepted the updates and accepted any new
              applicable terms.
              <br />
              <br />
              3. You acknowledge that we will process your Personal Data on the
              basis set out in our&nbsp;
              <Link
                href="https://www.jenesys.co/legal"
                target="_blank"
                color="blue.500"
                textDecor="underline"
              >
                Privacy Policy
              </Link>
              &nbsp; and accept that we will process some of your Personal Data
              in order to deliver the Services to you in accordance with the
              EULA. You must read our Privacy Policy. You can review our Privacy
              Policy at any time when using our Services. You warrant that any
              Personal Data that you provide to us is accurate, complete and up
              to date in all respects.
              <br />
              <br />
              4. You acknowledge and agree that internet transmissions are never
              completely private or secure and that any message or information
              you send using the Services may be read or intercepted by others,
              even if there is a special notice that a particular transmission
              is encrypted.
            </Text>
          </Box>
          <HStack mt={4} justify="flex-end">
            <Text fontSize={14} fontWeight={600}>
              Scroll to bottom
            </Text>
            <Circle
              onClick={scrollToBottom}
              cursor="pointer"
              color="white"
              size={38}
              bg="black"
            >
              <Icon fontSize={20} as={ChevronDownIcon} />
            </Circle>
          </HStack>
        </ModalBody>
        <ModalFooter>
          <VStack w="100%">
            <Button
              colorScheme="blue"
              mr={3}
              onClick={handleAccept}
              w="100%"
              isDisabled={!isScrolledToEnd}
              isLoading={isAcceptingTermsOfService}
            >
              Agree
            </Button>
          </VStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TermsOfService;
